import React, { useContext, PropsWithChildren, useState } from 'react';
import { Link, makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import MapIcon from '@material-ui/icons/MyLocation';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import MoneyIcon from '@material-ui/icons/MonetizationOn';
import LogoIcon from './LogoIcon';
import { NavLink } from 'react-router-dom';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  Sidebar,
  sidebarConfig,
  SidebarContext,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import {
  appThemeApiRef,
  configApiRef,
  microsoftAuthApiRef,
  ProfileInfo,
  useApi,
} from '@backstage/core-plugin-api';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import FullLogo from '../../assets/lighthouse_logo.svg';
import './Root.css';
/* eslint-disable */
import axios from 'axios';
/* eslint-enable */
import { useGetAPI } from '../../Utils/useGetAPI';
import { useAsync } from 'react-use';
import { FeatureFlagged } from '@backstage/core-app-api';
import AppsOutageIcon from '@mui/icons-material/AppsOutage';
import { datadogRum } from '@datadog/browser-rum';
import MoveDownIcon from '@mui/icons-material/MoveDown';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const useNavStylesDark = makeStyles({
  root: {
    '&:hover': {
      opacity: 1,
      backgroundColor: '#A9F6F8',
      color: '#A9F6F8',
      textDecoration: 'none',
    },
    '&:active': {
      textDecoration: 'none',
    },
    '&:focus': {
      textDecoration: 'none',
    },
  },
});

const useNavStylesLight = makeStyles({
  root: {
    '&:hover': {
      opacity: 1,
      backgroundColor: '#f2f2f2',
      color: '#f2f2f2',
      textDecoration: 'none',
    },
    '&:active': {
      textDecoration: 'none',
    },
    '&:focus': {
      textDecoration: 'none',
    },
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useContext(SidebarContext);

  return (
    <div className={classes.root}>
      <Link
        component={NavLink}
        to="/"
        underline="none"
        className={classes.link}
      >
        {isOpen ? (
          <img src={FullLogo} className="logo" alt="fireSpot" />
        ) : (
          <LogoIcon />
        )}
      </Link>
    </div>
  );
};

type listRes = {
  list: {
    id: string;
  }[];
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const themeApi = useApi(appThemeApiRef);
  const dark = useNavStylesDark();
  const light = useNavStylesLight();

  function getThemeID() {
    if (themeApi.getActiveThemeId() === 'dark-theme') {
      return dark;
    }
    return light;
  }

  const classes = getThemeID();

  const [radarArray, setRadarArray] = useState<string[]>([]);
  const [error, setError] = useState(false);

  const identityAPI = useApi(microsoftAuthApiRef);
  const config = useApi(configApiRef);

  datadogRum.init({
    applicationId: config.getString('app.datadogRum.applicationId'),
    clientToken: config.getString('app.datadogRum.clientToken'),
    site: config.getOptionalString('app.datadogRum.site'),
    env: config.getString('app.datadogRum.env'),
    sampleRate: 100,
    trackInteractions: true,
  });
  identityAPI.getProfile().then((info: ProfileInfo | undefined) => {
    if (info !== undefined) {
      datadogRum.setUserProperty('email', info.email);
    }
  });

  const url = useGetAPI();

  useAsync(async (): Promise<listRes> => {
    const token = await identityAPI.getIdToken();
    const response = await axios.get<listRes>(
      `${url}/v1/api/techradars/data/list`,
      {
        headers: {
          Authorization: `Basic ${token}`,
        },
      },
    );
    const data = await response.data;

    const radars = data?.list;

    function returnArray() {
      return radars
        ? radars.map((value: { id: string }) => {
            return radarArray.push(value.id);
          })
        : setError(true);
    }

    returnArray();

    return data;
  }, []);

  const radarDropdownArray = () => {
    const reformattedArray: { title: string; to: string }[] = [];

    radarArray.map(value => {
      const newObj = {
        title: value.charAt(0).toUpperCase() + value.slice(1),
        to: `tech-radar/${value}`,
      };

      reformattedArray.push(newObj);
    });

    return reformattedArray;
  };

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarItem icon={SearchIcon} to="/search" text="Search" />
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem
            icon={HomeIcon}
            to="/"
            text="Home"
            className={classes.root}
          />
          <SidebarItem
            icon={() => <CategoryIcon />}
            to="catalog"
            text="Catalog"
            className={classes.root}
          />
          {/* <FeatureFlagged with="show-catalog">
            <SidebarItem
              icon={() => <AccountTreeIcon />}
              to="catalog-graph?rootEntityRefs%5B%5D=group%3Adefault%2Forg-root-acuity-tech&maxDepth=∞&unidirectional=true&mergeRelations=true&direction=LR&showFilters=true"
              text="Catalog Graph"
              className={classes.root}
            />
          </FeatureFlagged> */}
          <SidebarItem
            icon={ExtensionIcon}
            to="api-docs?filters%5Bkind%5D=api&filters%5Buser%5D=all"
            text="APIs"
            className={classes.root}
          />
          <SidebarItem
            icon={() => <LibraryBooks />}
            to="docs"
            text="Docs"
            className={classes.root}
          />
          <SidebarItem
            icon={CreateComponentIcon}
            to="create"
            text="Templates"
            className={classes.root}
          />
          {/* End global nav */}

          <SidebarDivider />
          <SidebarSubmenuItem
            icon={MapIcon}
            title="Tech Radars"
            dropdownItems={radarDropdownArray()}
          />
          <SidebarItem
            icon={() => <PeopleIcon />}
            to="/catalog/default/group?filters%5Bkind%5D=group&filters%5Buser%5D=all"
            text="Squads"
            className={classes.root}
          />

          <FeatureFlagged with="show-argocd-notifications">
            <SidebarItem
              icon={() => <NotificationsIcon />}
              to="argocd-notifications"
              text="ArgoCD Notifications"
              className={classes.root}
            />
          </FeatureFlagged>

          <SidebarItem
            icon={() => <CoPresentIcon />}
            to="devops-projects"
            text="Projects"
            className={classes.root}
          />
          <SidebarItem
            icon={() => <AppsOutageIcon />}
            to="docs/default/component/devops-postmortem-techdocs"
            text="Postmortems"
            className={classes.root}
          />
          <FeatureFlagged with="show-struct">
            <SidebarItem
              icon={() => <MoveDownIcon />}
              to="structurizr-app"
              text="Structurizr App"
              className={classes.root}
            />
          </FeatureFlagged>
          <FeatureFlagged with="show-cost-insights">
            <SidebarItem
              icon={PeopleIcon}
              to="cost-insights"
              text="Cost Insights"
              className={classes.root}
            />
          </FeatureFlagged>
          <FeatureFlagged with="show-chatbot">
            <SidebarItem
              icon={() => <PeopleIcon />}
              to="chatbot"
              text="Chatbot"
              className={classes.root}
            />
          </FeatureFlagged>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarItem
          text="Settings"
          icon={() => <UserSettingsSignInAvatar />}
          to="/settings"
          className={classes.root}
        />
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
